import React, {FunctionComponent, memo, useCallback, useMemo, useEffect} from "react";
import cn from "classnames";
import styles from "./FillingModal.module.scss";
import {useStoreSystem} from "../../../store";
import shallow from "zustand/shallow";
import ItemGroup from "../../ItemGroup/ItemGroup";
import Item from "../../Item/Item";
import {LeftArrowThin} from "../../shared/icons";
import {Stack, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {ItemGroupI, SectionWidth} from "../../../shared";
import GridViewIcon from "../../icons/GridViewIcon";
import ListViewIcon from "../../icons/ListViewIcon";
import getAvailableSectionWidths from "../../../store/helpers/getAvailableSectionWidths";
import getElementsWidthsByGroup from "../../../store/helpers/getElementsWidthsByGroup";
import {SectionInterface} from "../../../store/types/store";
import {useShelvingStore} from "../../../store/useShelvingStore";
import LineStyleIcon from '@mui/icons-material/LineStyle';
import {LeftArrow} from "../../LeftArrow/LeftArrow";


interface FillingModalBodyProps {
    isBottom?: boolean;
    editSection?: SectionInterface;
}

const FillingModalBody: FunctionComponent<FillingModalBodyProps> = memo(({
                                                                             isBottom,
                                                                             editSection
                                                                         }) => {
    const {itemsGroup, setSystemProperties, isSectionFilling} = useStoreSystem((state) => ({
        itemsGroup: state.itemsGroup,
        setSystemProperties: state.setSystemProperties,
        isSectionFilling: state.sectionFilling.isFilling,
    }), shallow);

    const {getCurrentShelf, removeSpiritItems} = useShelvingStore((state) => ({
        getCurrentShelf: state.getCurrentShelving,
        removeSpiritItems: state.removeSpiritItems,
    }), shallow);

    const sections = getCurrentShelf().sections;
    const currentSection = isBottom ? sections[0] : editSection;
    const editSectionId = isBottom ? sections[0]?.id : editSection?.id;

    const [view, setView] = React.useState<'grid' | 'list'>('grid');
    const handleSetView = (newView: 'grid' | 'list') => () => {
        setView(newView)
    }
    const [widths, setWidths] = React.useState<number[]>([]);
    const handleChangeWidth = useCallback(
        (event: React.MouseEvent<HTMLElement>, newWidths: number[]) => {
            setWidths(newWidths);
        },
        []
    );

    const [images, setImages] = React.useState<string[]>([]);
    const [depths, setDepths] = React.useState<number[]>([200, 315, 520]);
    const handleDepths = useCallback(
        (event: React.MouseEvent<HTMLElement>, newDepths: number[]) => {
            setDepths(newDepths);
        },
        []
    );

    const [currentItemGroup, setCurrentItemGroup] = React.useState<ItemGroupI | null>(null)

    useEffect(() => {
        const availableSectionsWidths: Record<SectionWidth, SectionWidth> = {}
        sections.forEach((section, index, arr) => {
            if (!section.isIndent) {
                availableSectionsWidths[section.width] = section.width
                if (arr[index + 1] && !arr[index + 1].isIndent) {
                    availableSectionsWidths[arr[index + 1].width + section.width] = arr[index + 1].width + section.width
                }
            }
        })
        return setWidths(currentItemGroup?.ELEMENTS ? getElementsWidthsByGroup(currentItemGroup).filter(width => Boolean(availableSectionsWidths[width])) : [])
    }, [currentItemGroup])

    useEffect(() => {
        itemsGroup?.forEach((group) => {
            group?.ELEMENTS?.forEach((item) => {
                setImages((images) => [...images, item.PICTURE]);
            })
        })
    }, []);

    useEffect(() => {
        removeSpiritItems();
        return () => removeSpiritItems()
    }, [currentItemGroup]);

    useEffect(() => {
        if (!isSectionFilling) {
            removeSpiritItems();
        }
    }, [isSectionFilling])

    const availableSectionsWidths = useMemo(() => {
        const availableSectionsWidths: Record<SectionWidth, SectionWidth> = {}
        sections.forEach((section, index, arr) => {
            if (!section.isIndent) {
                availableSectionsWidths[section.width] = section.width
                if (arr[index + 1] && !arr[index + 1].isIndent) {
                    availableSectionsWidths[arr[index + 1].width + section.width] = arr[index + 1].width + section.width
                }
            }
        })
        return currentItemGroup?.ELEMENTS ? getElementsWidthsByGroup(currentItemGroup).filter(width => Boolean(availableSectionsWidths[width])) : []
    }, [currentItemGroup])


    const filteredItems = useMemo(() => {
        if (!currentItemGroup || !editSectionId || !currentSection) {
            return [];
        }
        const sectionWidths = isBottom ? widths : [currentSection.width]
        const availableWidths = getAvailableSectionWidths(sections, sections.findIndex((s) => s.id === currentSection.id));
        return currentItemGroup?.ELEMENTS?.length > 0
            ? currentItemGroup.ELEMENTS.filter(
                (item) =>
                    (isBottom ? sectionWidths.includes(+item.PROPERTIES.LENGTH) : availableWidths.includes(+item.PROPERTIES.LENGTH)) &&
                    (currentItemGroup.NAME.toLowerCase().includes("полки")
                        ? depths.some((depth) => depth === +item.PROPERTIES.DEPTH)
                        : true)
            ).map((item) => {
                return <Item
                    key={item.ID}
                    onClick={() => null}
                    item={item}
                    editSectionId={editSectionId}
                    isBottom={isBottom}
                    view={view}
                />;
            })
            : [];
    }, [currentItemGroup, editSectionId, currentSection, depths, widths, view, sections]);

    return (
        <div className={cn(styles.root, isBottom && styles.isBottom, styles[view])}>
            {currentItemGroup ? (
                <>
                    <div className={styles.header}>
                        <LeftArrow onClick={() => setCurrentItemGroup(null)}/>
                        <span className={styles.groupName}>
                            {currentItemGroup.NAME}
                            {(isBottom && currentItemGroup?.NAME?.toLowerCase().includes("полки")) && '. Глубина, мм:'}
                        </span>
                        {currentItemGroup?.NAME?.toLowerCase().includes("полки") && (
                            <ToggleButtonGroup
                                color={"primary"}
                                value={depths}
                                onChange={handleDepths}
                                aria-label="items depth"
                            >
                                <ToggleButton className={styles.tab} value={200} aria-label="200">
                                    {200}
                                </ToggleButton>
                                <ToggleButton className={styles.tab} value={315} aria-label="315">
                                    {315}
                                </ToggleButton>
                                <ToggleButton className={styles.tab} value={520} aria-label="520">
                                    {520}
                                </ToggleButton>
                            </ToggleButtonGroup>
                        )}
                        {isBottom && (
                            <>
                                <span className={styles.groupName}>
                                    Ширина, мм:
                                </span>
                                <ToggleButtonGroup
                                    color="primary"
                                    value={widths}
                                    onChange={handleChangeWidth}
                                    aria-label="section width"
                                >
                                    {availableSectionsWidths.map((w) => (
                                        <ToggleButton key={w} className={styles.tab} value={w} aria-label={`${w}`}>
                                            {w}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                                <Stack direction="row" spacing={1.2} ml={3}>
                                    <button
                                        className={cn(styles.viewToggle, view === 'grid' && styles.viewToggleActive)}
                                        onClick={handleSetView('grid')}
                                    >
                                        <GridViewIcon/>
                                    </button>
                                    <button
                                        className={cn(styles.viewToggle, view === 'list' && styles.viewToggleActive)}
                                        onClick={handleSetView('list')}
                                    >
                                        <ListViewIcon/>
                                    </button>
                                    <button
                                        className={cn(styles.viewToggle, isSectionFilling && styles.viewToggleActive)}
                                        onClick={() => {
                                            setSystemProperties([{
                                                name: 'sectionFilling',
                                                subName: 'isFilling',
                                                value: !isSectionFilling
                                            }])
                                        }}
                                    >
                                        <LineStyleIcon/>
                                    </button>
                                </Stack>
                            </>
                        )}
                    </div>
                    <div className={cn(styles.body, styles.items)}>
                        {filteredItems.length > 0 ? filteredItems : <b>Доступные элементы отсутствуют</b>}
                    </div>
                </>
            ) : (
                <>
                    {!isBottom && (
                        <div className={styles.header}>{`Выберите тип навесного элемента:`}</div>
                    )}
                    <div className={cn(styles.body, styles.categories)}>
                        {itemsGroup.length > 0 &&
                            itemsGroup.map((itemGroup) => {
                                //убираю из списка запчасти
                                if (/запчас/gi.test(itemGroup.NAME)) {
                                    return null;
                                }
                                return (
                                    <ItemGroup
                                        key={itemGroup.ID}
                                        onClick={() => setCurrentItemGroup(itemGroup)}
                                        itemGroup={itemGroup}
                                        small={isBottom}
                                    />
                                );
                            })}
                    </div>
                </>
            )
            }

            {/*   {images && images.map((image) => <img key={image} style={{ display: 'none' }} src={image} />)}  //FIXME это что то типа кеша для картинок что бы они сразу были..., странно что картинки кеша броузера грузятся с задержкой */}
        </div>
    );
});

export default FillingModalBody;
